import {Item, ItemTier, ItemType, ItemTypeDetail, SimpleItem, WeaponCategory} from '../../generated/types';
import {weaponCategoryToString} from '../enums';
import {humanEnumeration, lowerizeFirstLetter, onlyUnique} from '../utils';
import {ItemRarityNames} from './item-rarity-names';
import {ItemSubTypeNames, ItemTypeDetailNames, ItemTypeNames} from './item-type-names';

type ItemAttuneTag = {
	class?: string;
	spellcasting?: true;
	race?: string;
};

const ItemAttuneClasses: Record<string, string> = {
	bard: 'барда',
	barbarian: 'варвара',
	cleric: 'жреца',
	druid: 'друида',
	fighter: 'воина',
	monk: 'монаха',
	paladin: 'паладина',
	ranger: 'следопыта',
	rogue: 'плута',
	sorcerer: 'чародея',
	warlock: 'колдуна',
	wizard: 'волшебника',
};

const ItemAttuneRaces: Record<string, string> = {
	aasimar: 'аасимара',
	dwarf: 'дварфа',
	elf: 'эльфа',
	halfling: 'полурослика',
	human: 'человека',
	dragonborn: 'драконорожденного',
	gnome: 'гнома',
	goliath: 'голиафа',
	orc: 'орка',
	tiefling: 'тифлинга',
};

function parseItemAttuneTags(reqAttuneTags: ItemAttuneTag[]) {
	const classes = reqAttuneTags
		.map((tag) => tag.class)
		.map((c) => c && (ItemAttuneClasses[c] || c))
		.filter(onlyUnique)
		.filter(Boolean) as string[];

	const races = reqAttuneTags
		.map((tag) => tag.race)
		.map((r) => r && (ItemAttuneRaces[r] || r))
		.filter(onlyUnique)
		.filter(Boolean) as string[];

	const spellcasting = reqAttuneTags.find((tag) => tag.spellcasting);
	if (spellcasting) return 'заклинателя';

	return humanEnumeration([...classes, ...races], 'или');
}

type ItemRequires = {
	types?: ItemTypeDetail[];
	items?: SimpleItem[];
	weaponCategories?: WeaponCategory[];
};

function toItemLinks(items: SimpleItem[]) {
	return humanEnumeration(
		items.map((item) => {
			return `{@item ${item.id}, ${item.title}}`;
		}),
		'или',
	);
}

function parseItemRequires(requires: ItemRequires, excludes?: ItemRequires, type?: ItemType) {
	let result = '';

	if (requires.types) {
		const firstSubType = requires.types.at(0);
		const subTypeAnyTherm =
			firstSubType && ItemTypeDetailNames[firstSubType]?.subTypeAnyTherm != null
				? ItemTypeDetailNames[firstSubType].subTypeAnyTherm
				: type && ItemTypeNames[type]?.subTypeAnyTherm
				? ItemTypeNames[type].subTypeAnyTherm
				: '';
		result +=
			(subTypeAnyTherm ? `${subTypeAnyTherm} ` : '') +
			humanEnumeration(
				requires.types.map((type) => ItemTypeDetailNames[type]?.subType || ''),
				'или',
			);
		if (excludes?.items) result += `, но не ` + toItemLinks(excludes.items);
	}
	if (requires.items) result += toItemLinks(requires.items);
	if (requires.weaponCategories)
		result +=
			`любое ` +
			humanEnumeration(
				requires.weaponCategories.map((category) => {
					return weaponCategoryToString(category);
				}),
				'или',
			);

	return result;
}

export function getItemSubTitle(item: Item) {
	const type = item.type && ItemTypeNames[item.type];
	const typeGenus: 'male' | 'female' | 'other' | 'multiple' | null = type ? type.genus : 'male';
	const rarity = item.rarity && typeGenus && ItemRarityNames[item.rarity] && ItemRarityNames[item.rarity][typeGenus];
	let typeName = item.wondrous ? 'Чудесный предмет' : type?.title ? type.title : null;

	const baseItem = item.baseItem || item.parentItem?.baseItem;
	const subTypeDefault = item.subType?.length === 1 ? ItemSubTypeNames[item.subType[0]].default : undefined;

	if (baseItem) {
		typeName += ` ({@item ${lowerizeFirstLetter(baseItem.id)}, ${baseItem.title}})`;
	} else if (item.requires) {
		typeName += ` (${parseItemRequires(item.requires, item.excludes, item.type)})`;
	} else if (subTypeDefault) {
		typeName += ` (${subTypeDefault})`;
	} else if (item.type === ItemType.Poison) {
		typeName += ` (${ItemPoisonTypes[item.poisonType || 'unknown']})`;
	}

	item.reqAttune;
	let attuneText = '';
	if (item.reqAttune) {
		attuneText = `требует Настроенности${item.reqAttuneTags ? ' ' + parseItemAttuneTags(item.reqAttuneTags) : ''}`;
	}

	if (typeName)
		return `${typeName}${rarity ? `, ${rarity}` : ''}${attuneText ? ` (${attuneText})` : ''}${
			item.age ? `, ${ItemAges[item.age]}` : ''
		}${item.tier ? `, ${item.tier === ItemTier.Major ? 'значимый' : 'незначимый'} предмет` : ''}`;
	return null;
}

const ItemAges = {
	Renaissance: 'старинное оружие',
	Modern: 'современное оружие',
	Futuristic: 'футуристическое оружие',
};

const ItemPoisonTypes = {
	Ingested: 'поглощаемый',
	Inhaled: 'вдыхаемый',
	Contact: 'контактный',
	Injury: 'оружейный',
	unknown: 'неизвестный',
};
